import { useEffect, useMemo } from "react"
import { APIS } from "../../../config/apis"
import { ALERT_TYPE, REQUEST_METHOD, ShowToastMessage } from '@laramie/ui';
import { TIPO_MOVIMIENTO_CAJA } from "../../../consts/tipoMovimientoCaja"
import { MEDIO_PAGO } from "../../../consts/medioPago"
import { VALIDATION_MESSAGE } from "../../../consts/validationMessage"
import { ServerRequest } from "../../../utils/apiweb"
import { onRequestError, onRequestNoSuccess } from "../../../utils/requests"
import { isValidNumber } from "../../../utils/validator"
import { FormError, FormLabel, InputNumber, Modal } from "../../common"
import { useForm, useFormErrors, useManagedContext } from "../../hooks"
import { precisionRound } from "../../../utils/convert"


const MovimientoCajaModal = ({ idTipoMovimientoCaja, cajaResumen, onCancel, onConfirm }) => {
    const { setIsLoading } = useManagedContext()

    const [formValues, formHandle, , formSet] = useForm({
        efectivoDisponible: 0,
        efectivoOperacion: 0,
        observacion: ""
    })

    const efectivoRestante = useMemo(() => {
        if (idTipoMovimientoCaja === TIPO_MOVIMIENTO_CAJA.INGRESO)
            return precisionRound(formValues.efectivoDisponible + formValues.efectivoOperacion)
        else if (idTipoMovimientoCaja === TIPO_MOVIMIENTO_CAJA.RETIRO)
            return precisionRound(formValues.efectivoDisponible - formValues.efectivoOperacion)
        return 0
    }, [formValues.efectivoDisponible, formValues.efectivoOperacion])

    useEffect(() => {
        let efectivoDisponible = cajaResumen.asignacion.importeSaldoInicial;
        for (let m=0; m<cajaResumen.movimientos.length; m++) {
            const movimiento = cajaResumen.movimientos[m];
            if (movimiento.anulado) continue;
            if (movimiento.idTipoMovimientoCaja === TIPO_MOVIMIENTO_CAJA.COBRANZA) {
                for (let mp=0; mp<movimiento.mediosPagos.length; mp++) {
                    const medioPago = movimiento.mediosPagos[mp];
                    if (medioPago.idMedioPago === MEDIO_PAGO.EFECTIVO) {
                        efectivoDisponible += medioPago.importeCobro;
                    }
                }
            }
            else if (movimiento.idTipoMovimientoCaja === TIPO_MOVIMIENTO_CAJA.RETIRO) {
                efectivoDisponible -= movimiento.importeCobro;
            }
            else if (movimiento.idTipoMovimientoCaja === TIPO_MOVIMIENTO_CAJA.INGRESO) {
                efectivoDisponible += movimiento.importeCobro;
            }
        }
        efectivoDisponible = precisionRound(efectivoDisponible)
        formSet({...formValues, efectivoDisponible: efectivoDisponible, efectivoOperacion: 0, observacion: ""})
    }, [cajaResumen]);

    const AplicarOperacion = (importe) => {

        setIsLoading(true)

        const operacion = getData().path;
        const paramsUrl = `/movimiento/${operacion}/${cajaResumen.caja.id}`
        const dataBody = {
            importe: formValues.efectivoOperacion,
            observacion: formValues.observacion
        }

        ServerRequest(
            REQUEST_METHOD.PUT,
            null,
            true,
            APIS.URLS.CAJA,
            paramsUrl,
            dataBody,
            res => res.json().then(({idMovimientoCaja}) => {
                console.log(idMovimientoCaja) //TODO: emitir comprobante
                onConfirm();
                setIsLoading(false)
            }),
            res => {
                onRequestNoSuccess(res)
                setIsLoading(false)
            },
            err => {
                onRequestError(err)
                setIsLoading(false)
            },
        )
    }

    const getData = () => {
        const operacion =
        (idTipoMovimientoCaja === TIPO_MOVIMIENTO_CAJA.INGRESO) ? {
            path: "ingreso",
            labelControl: "Importe a ingresar",
            labelButton: "Ingresar",
            title: "Ingreso de dinero",
            result: () => precisionRound(formValues.efectivoDisponible + formValues.efectivoOperacion)
        } :
        (idTipoMovimientoCaja === TIPO_MOVIMIENTO_CAJA.RETIRO) ? {
            path: "retiro",
            labelControl: "Importe a retirar",
            labelButton: "Retirar",
            title: "Retiro de dinero",
            result: () => precisionRound(formValues.efectivoDisponible - formValues.efectivoOperacion)
        } :
        {
            path: "",
            labelButton: "",
            title: "",
            result: () => 0
        }

        return operacion
    }

    const getErrors = () => {
        const errors = {}
        if (!isValidNumber(formValues.efectivoOperacion, true)) errors.efectivoOperacion = VALIDATION_MESSAGE.REQUIRED
        return errors
    }

    const getFormatErrors = () => {
        const errors = {}
        if (getData().result() < 0) errors.efectivoOperacion = 'El restante no puede ser negativo'
        return errors
    }

    const { errorOf, validate } = useFormErrors(getErrors, getFormatErrors)

    const onClickConfirm = () => {
        if (!validate()) return

        AplicarOperacion()
    }

    return (
        <Modal
            onEscape={onCancel}
            size="lg"
            title={getData().title}
            body={(
                <div className='row form-basic'>
                    <div className="col-12 col-lg-4">
                        <label htmlFor="efectivoDisponible" className="form-label">Efectivo disponible</label>
                        <InputNumber
                            precision={2}
                            name="efectivoDisponible"
                            placeholder=""
                            className="form-control"
                            value={formValues.efectivoDisponible}
                            disabled={true}
                        />
                        <FormError />
                    </div>
                    <div className="col-12 col-lg-4">
                        <FormLabel htmlFor="efectivoOperacion" required>{getData().labelControl}</FormLabel>
                        <InputNumber
                            precision={2}
                            name="efectivoOperacion"
                            placeholder=""
                            className={`form-control ${errorOf('efectivoOperacion') ? 'is-invalid' : ''}`}
                            value={formValues.efectivoOperacion}
                            onChange={formHandle}
                        />
                        <FormError message={errorOf('efectivoOperacion')} />
                    </div>
                    <div className="col-12 col-lg-4">
                        <label htmlFor="efectivoRestante" className="form-label">Efectivo restante</label>
                        <InputNumber
                            precision={2}
                            name="efectivoRestante"
                            placeholder=""
                            className="form-control"
                            value={efectivoRestante}
                            disabled={true}
                        />
                        <FormError />
                    </div>
                    <div className="col-12">
                        <label htmlFor="observacion" className="form-label">Observación</label>
                        <input
                            type="text"
                            name="observacion"
                            placeholder=""
                            className="form-control"
                            value={formValues.observacion}
                            onChange={formHandle}
                            maxLength={250}
                        />
                        <FormError />
                    </div>
                </div>
            )}
            footer={(
                <div className="width-100p disp-b">
                    <button className="btn back-button m-left-5" data-dismiss="modal" onClick={onCancel}>Volver</button>
                    <button className="btn action-button" data-dismiss="modal" onClick={onClickConfirm}>{getData().labelButton}</button>
                </div>
            )}
        />
    )
}

export default MovimientoCajaModal
